import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Check, AlertCircle, Paperclip, AlertTriangle, RefreshCw, ShieldAlert } from 'lucide-react';
import {
  ParsedTransaction,
  CDBData,
  DASData,
  PayrollData,
  FGTSRelatorioData,
  DARFFederalData,
  FGTSGuiaData,
  LiquidosData,
  NFXmlData,
  getDeclaredAccounts,
  getEsocialStatus,
  syncEsocial,
  commitEsocialPreview,
  importEsocialZip,
  EsocialZipResult,
  ingestEsocialFolha,
  ingestEsocialCpp,
  parseImportFile,
  commitImport,
  ingestOfx,
  commitCDBImport,
  commitCEFLoanImport,
  commitNFImport,
  commitPayrollImport,
  validatePayroll,
  EsocialStatus,
  EsocialSyncResult,
  EsocialCommitResult,
  EsocialPreviewTransaction,
} from '../services/apiService';
import { uploadDocument, uploadAndLinkMulti } from '../services/documentService';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

type Step = 'upload' | 'preview' | 'preview-cdb' | 'preview-das' | 'preview-payroll' | 'preview-fgts' | 'preview-darf' | 'preview-fgts-guia' | 'preview-liquidos' | 'preview-cef-loan' | 'preview-nf' | 'preview-batch' | 'done';

const BRAZILIAN_PREPOSITIONS = new Set(['de', 'da', 'do', 'dos', 'das', 'e']);

function formatBrazilianName(name: string): string {
  return name.split(' ').map((w, i) => {
    const lower = w.toLowerCase();
    if (i > 0 && BRAZILIAN_PREPOSITIONS.has(lower)) return lower;
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }).join(' ');
}

const PAYROLL_FORMATS = ['pdf_holerite', 'pdf_fgts_relatorio', 'pdf_darf_federal', 'pdf_fgts_guia', 'pdf_liquidos', 'pdf_auto', 'pdf_cef_loan'];
const NO_SOURCE_ACCOUNT_FORMATS = [...PAYROLL_FORMATS, 'nf_xml'];

function defaultPerApur(): string {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

export default function ImportPage() {
  const navigate = useNavigate();
  const [activeImportMode, setActiveImportMode] = useState<'file' | 'esocial'>('file');
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [format, setFormat] = useState('csv');
  const [sourceAccount, setSourceAccount] = useState('');
  const [accounts, setAccounts] = useState<string[]>([]);

  // CSV options
  const [separator, setSeparator] = useState(',');
  const [dateCol, setDateCol] = useState(0);
  const [descCol, setDescCol] = useState(1);
  const [amountCol, setAmountCol] = useState(2);
  const [dateFormat, setDateFormat] = useState('%d/%m/%Y');
  const [skipHeader, setSkipHeader] = useState(true);

  // Parsed transactions with categories
  const [transactions, setTransactions] = useState<(ParsedTransaction & {
    category: string;
    skip: boolean;
    duplicate_of: string;
    duplicate_kind?: string;
    duplicate_date?: string;
    dedup_acked?: boolean;
    ofx_fitid?: string;
    ofx_account?: string;
    dedup_fingerprint?: string;
  })[]>([]);
  const [bulkDuplicate, setBulkDuplicate] = useState(false);
  const [fitidSkipCount, setFitidSkipCount] = useState(0);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ created: number; errors: { index: number; error: string }[] } | null>(null);
  const [employeeSync, setEmployeeSync] = useState<{ created: any[]; updated: any[]; deactivated: any[] } | null>(null);
  const [saldo, setSaldo] = useState<{ saldo_anterior: number; saldo_final_pdf: number | null; saldo_final_calculado: number; ok: boolean } | null>(null);
  const [cdbData, setCdbData] = useState<CDBData | null>(null);
  const [cdbReplace, setCdbReplace] = useState(false);
  const [dasData, setDasData] = useState<DASData | null>(null);
  const [dasReplace, setDasReplace] = useState(false);
  const [docAttached, setDocAttached] = useState<boolean | null>(null);
  const [docAttachError, setDocAttachError] = useState('');

  // Payroll state
  const [payrollData, setPayrollData] = useState<PayrollData | null>(null);
  const [payrollSkip, setPayrollSkip] = useState<Record<string, boolean>>({});
  const [payrollWarnings, setPayrollWarnings] = useState<string[]>([]);
  const [fgtsData, setFgtsData] = useState<FGTSRelatorioData | null>(null);
  const [darfData, setDarfData] = useState<DARFFederalData | null>(null);
  const [fgtsGuiaData, setFgtsGuiaData] = useState<FGTSGuiaData | null>(null);
  const [liquidosData, setLiquidosData] = useState<LiquidosData | null>(null);
  const [cefLoanData, setCefLoanData] = useState<any>(null);
  const [nfData, setNfData] = useState<NFXmlData | null>(null);
  const [nfClearProvisao, setNfClearProvisao] = useState(true);
  const [nfClearAdiantamento, setNfClearAdiantamento] = useState(false);

  // eSocial state
  const [esocialStatus, setEsocialStatus] = useState<EsocialStatus | null>(null);
  const [esocialResult, setEsocialResult] = useState<EsocialSyncResult | null>(null);
  const [esocialCommitResult, setEsocialCommitResult] = useState<EsocialCommitResult | null>(null);
  const [esocialPerApur, setEsocialPerApur] = useState(defaultPerApur());
  const [esocialForce, setEsocialForce] = useState(false);
  const [esocialLoading, setEsocialLoading] = useState(false);
  const [esocialSyncing, setEsocialSyncing] = useState(false);
  const [esocialCommitting, setEsocialCommitting] = useState(false);
  const [esocialZipUploading, setEsocialZipUploading] = useState(false);
  const [esocialZipResult, setEsocialZipResult] = useState<EsocialZipResult | null>(null);
  const [esocialIngesting, setEsocialIngesting] = useState(false);
  const [esocialIngestMsg, setEsocialIngestMsg] = useState('');

  // Batch upload state
  const [batchFiles, setBatchFiles] = useState<File[]>([]);
  const [batchPayroll, setBatchPayroll] = useState<PayrollData | null>(null);
  const [batchPayrollSkip, setBatchPayrollSkip] = useState<Record<string, boolean>>({});
  const [batchFgts, setBatchFgts] = useState<FGTSRelatorioData | null>(null);
  const [batchDarf, setBatchDarf] = useState<DARFFederalData | null>(null);
  const [batchFgtsGuia, setBatchFgtsGuia] = useState<FGTSGuiaData | null>(null);
  const [batchLiquidos, setBatchLiquidos] = useState<LiquidosData | null>(null);
  const [batchFileMap, setBatchFileMap] = useState<Record<string, File>>({});
  const [batchWarnings, setBatchWarnings] = useState<string[]>([]);

  // Category accounts (all except the source account itself)
  const categoryAccounts = accounts.filter(
    (a) => a !== sourceAccount && !a.startsWith('Patrimônio:Lucros') && a !== 'Ativos' && a !== 'Despesas' && a !== 'Receitas' && a !== 'Patrimônio' && a !== 'Passivo'
  );

  // Source accounts (Ativos:Banco:*)
  const sourceAccounts = accounts.filter((a) => a.startsWith('Ativos:'));

  useEffect(() => {
    getDeclaredAccounts()
      .then((res) => {
        setAccounts(res.accounts);
        const defaultSource = res.accounts.find((a) => a.startsWith('Ativos:Banco'));
        if (defaultSource) setSourceAccount(defaultSource);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (activeImportMode !== 'esocial') return;
    loadEsocialStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeImportMode]);

  const loadEsocialStatus = async () => {
    setEsocialLoading(true);
    setError('');
    try {
      const status = await getEsocialStatus();
      setEsocialStatus(status);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao carregar status eSocial');
    } finally {
      setEsocialLoading(false);
    }
  };

  const handleEsocialSync = async () => {
    setEsocialSyncing(true);
    setError('');
    setEsocialResult(null);
    setEsocialCommitResult(null);
    try {
      const result = await syncEsocial({
        per_apur: esocialPerApur,
        force: esocialForce,
        commit: false,
      });
      setEsocialResult(result);
      await loadEsocialStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao sincronizar eSocial');
    } finally {
      setEsocialSyncing(false);
    }
  };

  const handleEsocialZipUpload = async (file: File) => {
    setEsocialZipUploading(true);
    setError('');
    setEsocialZipResult(null);
    try {
      const result = await importEsocialZip(file);
      setEsocialZipResult(result);
      // Se houver uma única competência, já preenche o período pra facilitar.
      if (result.per_apurs.length === 1) {
        setEsocialPerApur(result.per_apurs[0]);
      }
      await loadEsocialStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao importar zip do eSocial');
    } finally {
      setEsocialZipUploading(false);
    }
  };

  const handleIngestFolha = async () => {
    if (!esocialPerApur) return;
    setEsocialIngesting(true);
    setError('');
    setEsocialIngestMsg('');
    try {
      const r = await ingestEsocialFolha(esocialPerApur);
      const semCadastro = r.no_mapping.length;
      const duplicados = Math.max(0, r.skipped - semCadastro);
      let msg = `Folha ${r.per_apur}: ${r.created} criado(s), ${duplicados} já existente(s) (${r.total_cpfs} trabalhadores)`;
      if (semCadastro) {
        msg += `. ${semCadastro} sem cadastro de funcionário (CPF não encontrado em Funcionários): ${r.no_mapping.join(', ')}`;
      }
      setEsocialIngestMsg(msg);
      await loadEsocialStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao importar folha eSocial');
    } finally {
      setEsocialIngesting(false);
    }
  };

  const handleIngestCpp = async () => {
    if (!esocialPerApur) return;
    setEsocialIngesting(true);
    setError('');
    setEsocialIngestMsg('');
    try {
      const r = await ingestEsocialCpp(esocialPerApur);
      setEsocialIngestMsg(`CPP+Terceiros ${r.per_apur}: ${r.created} lançamento(s) criado(s), ${r.skipped} já existente(s)`);
      await loadEsocialStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao importar CPP eSocial');
    } finally {
      setEsocialIngesting(false);
    }
  };

  const getEsocialPreviewTransactions = (): EsocialPreviewTransaction[] => (
    esocialResult?.parsed?.flatMap((event) => event.transactions || []) || []
  );

  const handleEsocialCommitPreview = async () => {
    const transactionsToCommit = getEsocialPreviewTransactions();
    if (transactionsToCommit.length === 0) {
      setError('Nenhum lançamento eSocial para confirmar');
      return;
    }
    setEsocialCommitting(true);
    setError('');
    setEsocialCommitResult(null);
    try {
      const result = await commitEsocialPreview({
        per_apur: esocialResult?.per_apur || esocialPerApur,
        transactions: transactionsToCommit,
      });
      setEsocialCommitResult(result);
      await loadEsocialStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao confirmar lançamentos eSocial');
    } finally {
      setEsocialCommitting(false);
    }
  };

  const handleSingleParseResult = (data: any) => {
    if (data.das) {
      setDasData(data.das);
      setDasReplace(!!data.duplicate_of);
      setStep('preview-das');
    } else if (data.cdb) {
      setCdbData(data.cdb);
      setCdbReplace(!!data.duplicate_of);
      setStep('preview-cdb');
    } else if (data.payroll) {
      setPayrollData(data.payroll);
      setPayrollSkip(Object.fromEntries(data.payroll.funcionarios.map((f: any) => [f.nome, false])));
      setPayrollWarnings(data.warnings || []);
      setStep('preview-payroll');
    } else if (data.fgts_relatorio) {
      setFgtsData(data.fgts_relatorio);
      setPayrollWarnings(data.warnings || []);
      setStep('preview-fgts');
    } else if (data.darf_federal) {
      setDarfData(data.darf_federal);
      setPayrollWarnings(data.warnings || []);
      setStep('preview-darf');
    } else if (data.fgts_guia) {
      setFgtsGuiaData(data.fgts_guia);
      setStep('preview-fgts-guia');
    } else if (data.liquidos) {
      setLiquidosData(data.liquidos);
      setStep('preview-liquidos');
    } else if (data.cef_loan) {
      setCefLoanData(data.cef_loan);
      setStep('preview-cef-loan');
    } else if (data.nf_xml) {
      setNfData(data.nf_xml);
      setNfClearProvisao(data.nf_xml.has_provisao);
      setNfClearAdiantamento(data.nf_xml.has_adiantamento);
      setStep('preview-nf');
    } else {
      setTransactions(data.transactions.map((t: any) => ({
        ...t,
        category: t.suggested_category || '',
        duplicate_of: t.duplicate_of || '',
        duplicate_kind: t.duplicate_kind || '',
        duplicate_date: t.duplicate_date || '',
        dedup_acked: false,
        ofx_fitid: t.ofx_fitid || '',
        ofx_account: t.ofx_account || '',
        dedup_fingerprint: t.dedup_fingerprint || '',
        // FITID-exact matches default to skipped (will be auto-skipped server-side anyway)
        skip: !!t.duplicate_of,
      })));
      setBulkDuplicate(!!data.bulk_duplicate);
      setFitidSkipCount(data.fitid_skip_count || 0);
      setSaldo(data.saldo || null);
      setStep('preview');
    }
  };

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    setError('');
    try {
      // === Novo fluxo unificado de OFX via Pendentes ===
      // Fase 1.3 do plano de unificação. OFX vai direto pra fila de pendentes;
      // pipeline auto-classifica HIGH confidence; usuário revisa o resto em /pendentes.
      if (format === 'ofx') {
        if (!sourceAccount) {
          setError('Selecione uma conta de origem antes de importar OFX');
          setParsing(false);
          return;
        }
        const result = await ingestOfx(file, {
          source_account: sourceAccount,
          auto_classify: true,
        });
        // Mostra resumo + redireciona pra /pendentes
        const summary =
          `${result.ingested} importadas (${result.skipped} duplicatas). ` +
          `${result.auto_committed} auto-classificadas; ` +
          `${result.stay_pending} pra revisar.`;
        // Persiste mensagem no sessionStorage pra /pendentes mostrar como banner
        sessionStorage.setItem('lastIngestSummary', summary);
        navigate('/pendentes');
        return;
      }

      // Batch mode: parse multiple payroll PDFs
      if (batchFiles.length > 1) {
        let bPayroll: PayrollData | null = null;
        let bFgts: FGTSRelatorioData | null = null;
        let bDarf: DARFFederalData | null = null;
        let bFgtsGuia: FGTSGuiaData | null = null;
        let bLiquidos: LiquidosData | null = null;
        const allWarnings: string[] = [];
        const fileMap: Record<string, File> = {};

        for (const f of batchFiles) {
          const useFormat = format === 'pdf_auto' ? 'pdf_auto' : detectPdfFormat(f.name);
          const data = await parseImportFile(f, { format: useFormat }) as any;
          allWarnings.push(...(data.warnings || []));
          if (data.payroll) {
            bPayroll = data.payroll;
            fileMap['payroll'] = f;
          } else if (data.fgts_relatorio) {
            bFgts = data.fgts_relatorio;
            fileMap['fgts'] = f;
          } else if (data.darf_federal) {
            bDarf = data.darf_federal;
            fileMap['darf'] = f;
          } else if (data.fgts_guia) {
            bFgtsGuia = data.fgts_guia;
            fileMap['fgts_guia'] = f;
          } else if (data.liquidos) {
            bLiquidos = data.liquidos;
            fileMap['liquidos'] = f;
          }
        }

        // Cross-validate
        try {
          const validation = await validatePayroll({
            holerite: bPayroll,
            fgts_relatorio: bFgts,
            liquidos: bLiquidos,
            fgts_guia: bFgtsGuia,
          });
          allWarnings.push(...validation.warnings);
        } catch {
          // Non-blocking
        }

        setBatchPayroll(bPayroll);
        setBatchPayrollSkip(bPayroll ? Object.fromEntries(bPayroll.funcionarios.map(f => [f.nome, false])) : {});
        setBatchFgts(bFgts);
        setBatchDarf(bDarf);
        setBatchFgtsGuia(bFgtsGuia);
        setBatchLiquidos(bLiquidos);
        setBatchFileMap(fileMap);
        setBatchWarnings(allWarnings);
        setStep('preview-batch');
      } else {
        // Single file mode
        const res = await parseImportFile(file, {
          format,
          separator: format === 'csv' ? separator : undefined,
          date_col: format === 'csv' ? dateCol : undefined,
          description_col: format === 'csv' ? descCol : undefined,
          amount_col: format === 'csv' ? amountCol : undefined,
          date_format: format === 'csv' ? dateFormat : undefined,
          skip_header: format === 'csv' ? skipHeader : undefined,
          source_account: sourceAccount,
        });
        handleSingleParseResult(res);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao parsear arquivo');
    } finally {
      setParsing(false);
    }
  };

  const handleCategoryChange = (index: number, category: string) => {
    setTransactions((prev) =>
      prev.map((t, i) => (i === index ? { ...t, category } : t))
    );
  };

  const handleSetAllCategories = (category: string) => {
    setTransactions((prev) => prev.map((t) => (t.category || t.skip ? t : { ...t, category })));
  };

  const handleToggleSkip = (index: number) => {
    setTransactions((prev) =>
      prev.map((t, i) => (i === index ? { ...t, skip: !t.skip } : t))
    );
  };

  const handleCommit = async () => {
    const toImport = transactions.filter((t) => !t.skip);
    const uncategorized = toImport.filter((t) => !t.category);
    if (uncategorized.length > 0) {
      setError(`${uncategorized.length} transação(ões) sem conta`);
      return;
    }
    if (!sourceAccount) {
      setError('Selecione uma conta de origem');
      return;
    }

    setCommitting(true);
    setError('');
    try {
      const res = await commitImport(
        toImport.map((t) => ({
          date: t.date,
          description: t.description,
          amount: t.amount,
          category: t.category,
          source_account: sourceAccount,
          original_payee: (t as any).original_payee || t.description,
          ofx_fitid: t.ofx_fitid || undefined,
          ofx_account: t.ofx_account || undefined,
          dedup_fingerprint: t.dedup_fingerprint || undefined,
          duplicate_kind: t.duplicate_kind || undefined,
          duplicate_of: t.duplicate_of || undefined,
          // User chose to import despite the flag → ack it
          dedup_acked: !!t.duplicate_of,
        }))
      );
      setResult(res);
      setStep('done');
      // Attach source file to every transaction the parser identified —
      // newly committed AND duplicates that map to existing journal entries.
      if (file) {
        const targets: { date: string; payee: string }[] = [];
        // Committed: backend returns final {date, payee}
        for (const ct of (res as any).transactions || []) {
          targets.push({
            date: String(ct.date || '').replace(/\//g, '-'),
            payee: String(ct.payee || ''),
          });
        }
        // FITID auto-skipped: link to the existing journal entry (duplicate_of)
        for (const sk of (res as any).skipped || []) {
          if (sk.duplicate_of) {
            targets.push({
              date: String(sk.date || '').replace(/\//g, '-'),
              payee: String(sk.duplicate_of),
            });
          }
        }
        // User-skipped duplicates: attach to the existing journal entry.
        // Use duplicate_date (journal-side date) when available — for heuristic
        // matches the input date may differ from the journal date by a few days.
        for (const t of transactions) {
          if (t.skip && t.duplicate_of) {
            const dupDate = t.duplicate_date || t.date;
            targets.push({
              date: dupDate.replace(/\//g, '-'),
              payee: t.duplicate_of,
            });
          }
        }
        if (targets.length > 0) {
          try {
            const docType = format === 'ofx' ? 'extrato' : format === 'pdf_caixa' ? 'extrato' : 'comprovante';
            const desc = format === 'ofx' ? `Extrato OFX ${file.name}` : format === 'pdf_caixa' ? 'Extrato Caixa' : `Importação ${file.name}`;
            const linkRes = await uploadAndLinkMulti(file, docType, desc, targets);
            setDocAttached(!!linkRes && (linkRes.linked || 0) > 0);
            if (linkRes) {
              (res as any).docLinkCount = linkRes.linked;
              (res as any).docMissedCount = (linkRes.missed || []).length;
              setResult({ ...res });
            }
          } catch {
            setDocAttachError('Transações importadas, mas falha ao anexar documento');
            setDocAttached(false);
          }
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gravar transações');
    } finally {
      setCommitting(false);
    }
  };

  const handleCommitDAS = async () => {
    if (!dasData) return;
    setCommitting(true);
    setError('');
    try {
      const res = await commitCDBImport(dasData.transaction);
      setResult({ created: res.created, errors: res.errors });
      setStep('done');
      // Auto-attach the source PDF
      if (file) {
        try {
          const txnDate = dasData.transaction.date.replace(/\//g, '-');
          await uploadDocument(
            file, 'darf',
            `DAS Simples Nacional ${dasData.periodo}`,
            txnDate,
            dasData.transaction.description,
          );
          setDocAttached(true);
        } catch {
          setDocAttachError('Transação importada, mas falha ao anexar documento');
          setDocAttached(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gravar DAS');
    } finally {
      setCommitting(false);
    }
  };

  const handleCommitCDB = async () => {
    if (!cdbData) return;
    setCommitting(true);
    setError('');
    try {
      const res = await commitCDBImport(cdbData.transaction);
      setResult({ created: res.created, errors: res.errors });
      setStep('done');
      // Auto-attach the source PDF
      if (file) {
        try {
          const txnDate = cdbData.transaction.date.replace(/\//g, '-');
          await uploadDocument(
            file, 'comprovante',
            `Comprovante resgate CDB ${cdbData.produto}`,
            txnDate,
            cdbData.transaction.description,
          );
          setDocAttached(true);
        } catch {
          setDocAttachError('Transação importada, mas falha ao anexar documento');
          setDocAttached(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gravar transação CDB');
    } finally {
      setCommitting(false);
    }
  };

  const handleCommitPayroll = async () => {
    if (!payrollData) return;
    const skippedNames = new Set(Object.entries(payrollSkip).filter(([, v]) => v).map(([k]) => k));
    const txnsToCommit = payrollData.transactions.filter((txn, i) => {
      const func = payrollData.funcionarios[i];
      return func ? !skippedNames.has(func.nome) : true;
    });
    if (txnsToCommit.length === 0) {
      setError('Nenhuma transação selecionada');
      return;
    }
    setCommitting(true);
    setError('');
    try {
      const funcsToCommit = payrollData.funcionarios.filter(f => !skippedNames.has(f.nome));
      const res = await commitPayrollImport({
        competencia: payrollData.competencia,
        transactions: txnsToCommit,
        funcionarios: funcsToCommit,
      });
      setResult({ created: res.created, errors: res.errors });
      if ((res as any).employee_sync) {
        setEmployeeSync((res as any).employee_sync);
      }
      setStep('done');
      if (file) {
        try {
          const txnDate = txnsToCommit[0].date.replace(/\//g, '-');
          await uploadDocument(file, 'folha', `Holerite ${payrollData.competencia}`, txnDate, txnsToCommit[0].description);
          setDocAttached(true);
        } catch {
          setDocAttachError('Provisões importadas, mas falha ao anexar documento');
          setDocAttached(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gravar provisões');
    } finally {
      setCommitting(false);
    }
  };

  const handleCommitFGTS = async () => {
    if (!fgtsData) return;
    setCommitting(true);
    setError('');
    try {
      const res = await commitPayrollImport({
        competencia: fgtsData.competencia,
        transactions: [fgtsData.transaction],
      });
      setResult({ created: res.created, errors: res.errors });
      setStep('done');
      if (file) {
        try {
          const txnDate = fgtsData.transaction.date.replace(/\//g, '-');
          await uploadDocument(file, 'fgts', `Relatório FGTS ${fgtsData.competencia}`, txnDate, fgtsData.transaction.description);
          setDocAttached(true);
        } catch {
          setDocAttachError('Provisão importada, mas falha ao anexar documento');
          setDocAttached(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gravar provisão FGTS');
    } finally {
      setCommitting(false);
    }
  };

  const handleCommitDARF = async () => {
    if (!darfData) return;
    setCommitting(true);
    setError('');
    try {
      const res = await commitPayrollImport({
        competencia: darfData.competencia,
        transactions: [darfData.transaction],
      });
      setResult({ created: res.created, errors: res.errors });
      setStep('done');
      if (file) {
        try {
          const txnDate = darfData.transaction.date.replace(/\//g, '-');
          await uploadDocument(file, 'darf', `DARF Tributos Federais ${darfData.competencia}`, txnDate, darfData.transaction.description);
          setDocAttached(true);
        } catch {
          setDocAttachError('Provisão importada, mas falha ao anexar documento');
          setDocAttached(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gravar provisão tributos');
    } finally {
      setCommitting(false);
    }
  };

  const handleCommitBatch = async () => {
    setCommitting(true);
    setError('');
    try {
      // Collect all transactions to commit in a single batch
      const allTransactions: { date: string; description: string; postings: { account: string; amount: number; commodity: string }[] }[] = [];
      let competencia = '';

      if (batchPayroll) {
        competencia = batchPayroll.competencia;
        const skippedNames = new Set(Object.entries(batchPayrollSkip).filter(([, v]) => v).map(([k]) => k));
        batchPayroll.transactions.forEach((txn, i) => {
          const func = batchPayroll.funcionarios[i];
          if (!func || !skippedNames.has(func.nome)) allTransactions.push(txn);
        });
      }
      if (batchFgts) {
        if (!competencia) competencia = batchFgts.competencia;
        allTransactions.push(batchFgts.transaction);
      }
      if (batchDarf) {
        if (!competencia) competencia = batchDarf.competencia;
        allTransactions.push(batchDarf.transaction);
      }

      if (allTransactions.length === 0) {
        setError('Nenhuma transação para importar');
        setCommitting(false);
        return;
      }

      const res = await commitPayrollImport({
        competencia,
        transactions: allTransactions,
      });
      setResult({ created: res.created, errors: res.errors });
      setStep('done');

      // Attach source PDFs as documents
      try {
        const txnDate = allTransactions[0]?.date?.replace(/\//g, '-') || '';
        const docUploads: Promise<any>[] = [];
        if (batchFileMap['payroll'] && batchPayroll) {
          docUploads.push(uploadDocument(batchFileMap['payroll'], 'folha', `Holerite ${competencia}`, txnDate, allTransactions[0]?.description || ''));
        }
        if (batchFileMap['fgts'] && batchFgts) {
          docUploads.push(uploadDocument(batchFileMap['fgts'], 'fgts', `Relatório FGTS ${competencia}`, txnDate, batchFgts.transaction.description));
        }
        if (batchFileMap['darf'] && batchDarf) {
          docUploads.push(uploadDocument(batchFileMap['darf'], 'darf', `DARF Tributos Federais ${competencia}`, txnDate, batchDarf.transaction.description));
        }
        if (docUploads.length > 0) {
          await Promise.all(docUploads);
          setDocAttached(true);
        }
      } catch {
        setDocAttachError('Provisões importadas, mas falha ao anexar documentos');
        setDocAttached(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao gravar provisões');
    } finally {
      setCommitting(false);
    }
  };

  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setTransactions([]);
    setCdbData(null);
    setCdbReplace(false);
    setDasData(null);
    setDasReplace(false);
    setPayrollData(null);
    setPayrollSkip({});
    setPayrollWarnings([]);
    setFgtsData(null);
    setDarfData(null);
    setFgtsGuiaData(null);
    setLiquidosData(null);
    setCefLoanData(null);
    setBatchFiles([]);
    setBatchPayroll(null);
    setBatchPayrollSkip({});
    setBatchFgts(null);
    setBatchDarf(null);
    setBatchFgtsGuia(null);
    setBatchLiquidos(null);
    setBatchFileMap({});
    setBatchWarnings([]);
    setResult(null);
    setEmployeeSync(null);
    setError('');
    setDocAttached(null);
    setDocAttachError('');
  };

  const formatAmount = (amount: number) => {
    const formatted = Math.abs(amount).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return amount < 0 ? `-R$ ${formatted}` : `R$ ${formatted}`;
  };

  const detectPdfFormat = (name: string): string => {
    const lower = name.toLowerCase();
    if (lower.includes('holerite') || lower.includes('recibo') || lower.includes('folha')) return 'pdf_holerite';
    if (lower.includes('fgts') && (lower.includes('relat') || lower.includes('detalh'))) return 'pdf_fgts_relatorio';
    if (lower.includes('darf') || lower.includes('tribut')) return 'pdf_darf_federal';
    if (lower.includes('fgts') && (lower.includes('guia') || lower.includes('gfd'))) return 'pdf_fgts_guia';
    if (lower.includes('liquido') || lower.includes('líquido')) return 'pdf_liquidos';
    if (lower.includes('comprovante') || lower.includes('cdb') || lower.includes('renda fixa')) return 'pdf_cdb_santander';
    if (lower.includes('das') || lower.includes('simples')) return 'pdf_das';
    return 'pdf_caixa';
  };

  const renderEsocialPanel = () => {
    const recentEvents = esocialStatus?.recent_events || [];
    const latest = esocialStatus?.latest_sync;
    const previewTransactions = getEsocialPreviewTransactions();
    const createCount = previewTransactions.filter((txn) => (txn.action || 'create') === 'create').length;
    const replaceCount = previewTransactions.filter((txn) => txn.action === 'replace').length;
    return (
      <div className="space-y-4">
        {esocialStatus?.forbidden_window && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>Janela eSocial bloqueada até {esocialStatus.next_allowed_date}.</span>
          </div>
        )}

        {/* Importar zip do eSocial (download manual do portal) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Importar zip do eSocial</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Baixe o zip de eventos no portal do eSocial e importe aqui. Depois use
              "Importar por competência" abaixo (folha S-5001 / CPP S-5011).
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              type="file"
              accept=".zip"
              disabled={esocialZipUploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleEsocialZipUpload(f);
                e.target.value = '';
              }}
              className="block text-sm text-gray-700 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-600 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-700 disabled:opacity-50"
            />
            {esocialZipUploading && <LoadingSpinner />}
          </div>
          {esocialZipResult && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800 space-y-1">
              <p className="font-medium">
                {esocialZipResult.stored} evento(s) importado(s)
                {esocialZipResult.skipped > 0 && `, ${esocialZipResult.skipped} já existente(s)`}
                {' '}(de {esocialZipResult.total_xml} XML no zip)
              </p>
              {esocialZipResult.per_apurs.length > 0 && (
                <p>Competências encontradas: {esocialZipResult.per_apurs.join(', ')}</p>
              )}
              {Object.keys(esocialZipResult.by_tp_evt).length > 0 && (
                <p className="text-xs text-green-700">
                  {Object.entries(esocialZipResult.by_tp_evt).map(([t, n]) => `${t}: ${n}`).join(' · ')}
                </p>
              )}
              {esocialZipResult.errors.length > 0 && (
                <p className="text-xs text-amber-700">{esocialZipResult.errors.length} erro(s) ignorado(s)</p>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-xs text-gray-500">Última sync</p>
              <p className="text-sm font-semibold text-gray-900">
                {latest?.status || 'Nunca executada'}
              </p>
              {latest?.finished_at && (
                <p className="text-xs text-gray-500 mt-1">{new Date(latest.finished_at).toLocaleString('pt-BR')}</p>
              )}
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-xs text-gray-500">Eventos salvos</p>
              <p className="text-sm font-semibold text-gray-900">{esocialStatus?.events_count ?? 0}</p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-xs text-gray-500">Scheduler</p>
              <p className="text-sm font-semibold text-gray-900">{esocialStatus?.enabled ? 'Ativo' : 'Manual'}</p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-xs text-gray-500">Auto-commit</p>
              <p className="text-sm font-semibold text-gray-900">{esocialStatus?.auto_commit ? 'Ativo' : 'Desligado'}</p>
            </div>
          </div>

          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Período</label>
              <input
                type="month"
                value={esocialPerApur}
                onChange={(e) => setEsocialPerApur(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input
                type="checkbox"
                checked={esocialForce}
                onChange={(e) => setEsocialForce(e.target.checked)}
                className="rounded border-gray-300"
              />
              Forçar janela
            </label>
            <div className="flex-1" />
            <button
              onClick={loadEsocialStatus}
              disabled={esocialLoading || esocialSyncing || esocialCommitting}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${esocialLoading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
            <button
              onClick={handleEsocialSync}
              disabled={esocialSyncing || esocialCommitting || !esocialPerApur}
              className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {esocialSyncing ? <LoadingSpinner /> : <RefreshCw className="h-4 w-4" />}
              Sincronizar
            </button>
            <button
              onClick={handleIngestFolha}
              disabled={esocialIngesting || esocialSyncing || !esocialPerApur}
              title="Gera provisão de folha (INSS/IRRF/FGTS) da competência a partir dos eventos S-5001/5002/5003 importados"
              className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
            >
              {esocialIngesting ? <LoadingSpinner /> : null}
              Importar folha
            </button>
            <button
              onClick={handleIngestCpp}
              disabled={esocialIngesting || esocialSyncing || !esocialPerApur}
              title="Gera provisão de CPP+Terceiros (S-5011) da competência"
              className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
            >
              {esocialIngesting ? <LoadingSpinner /> : null}
              Importar CPP
            </button>
          </div>

          {esocialIngestMsg && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
              {esocialIngestMsg}
            </div>
          )}

          {esocialResult && (
            <div className={`rounded-lg p-3 text-sm ${
              esocialResult.status === 'ok'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : esocialResult.status === 'blocked'
                  ? 'bg-amber-50 text-amber-800 border border-amber-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              <p className="font-medium">
                Resultado: {esocialResult.status} — {esocialResult.events_downloaded} XML(s), {previewTransactions.length} ação(ões) proposta(s)
              </p>
              {esocialResult.warnings.map((warning, index) => (
                <p key={`w-${index}`} className="mt-1">{warning}</p>
              ))}
              {esocialResult.errors.map((item, index) => (
                <p key={`e-${index}`} className="mt-1">{item}</p>
              ))}
            </div>
          )}
        </div>

        {esocialResult && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Preview de lançamentos</h2>
                <p className="text-sm text-gray-500">
                  {createCount} provisão(ões) nova(s), {replaceCount} reclassificação(ões) de extrato para {esocialResult.per_apur}.
                </p>
              </div>
              <button
                onClick={handleEsocialCommitPreview}
                disabled={esocialCommitting || previewTransactions.length === 0}
                className="px-5 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {esocialCommitting ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
                Confirmar lançamentos
              </button>
            </div>

            {previewTransactions.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">
                Nenhum lançamento contábil proposto. Os XMLs baixados podem ser totalizadores, pagamentos ou eventos que exigem revisão manual.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Ação</th>
                      <th className="px-4 py-3 text-left font-medium">Data</th>
                      <th className="px-4 py-3 text-left font-medium">Descrição</th>
                      <th className="px-4 py-3 text-left font-medium">Postings</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {previewTransactions.map((txn, index) => (
                      <tr key={`${txn.description}-${index}`} className="hover:bg-blue-50 even:bg-gray-100 align-top">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                            txn.action === 'replace'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {txn.action === 'replace' ? 'Reclassificar extrato' : 'Criar provisão'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-700">{txn.date}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{txn.description}</p>
                          {txn.action === 'replace' && txn.matched_payee && (
                            <p className="text-xs text-amber-700 mt-1">
                              Substitui: {txn.matched_date || txn.date} — {txn.matched_payee}
                            </p>
                          )}
                          {txn.tags?.['esocial-event'] && (
                            <p className="text-xs text-gray-500 mt-1">Evento: {txn.tags['esocial-event']}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 min-w-[360px]">
                          <div className="space-y-1">
                            {txn.postings.map((posting, postingIndex) => (
                              <div key={postingIndex} className="flex items-center justify-between gap-4">
                                <span className="text-gray-700">{posting.account}</span>
                                <span className={`font-mono whitespace-nowrap ${posting.amount < 0 ? 'text-red-600' : 'text-green-700'}`}>
                                  {formatAmount(posting.amount)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {esocialCommitResult && (
              <div className="m-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                <p className="font-medium">
                  {esocialCommitResult.created} lançamento(s) criado(s), {esocialCommitResult.replaced || 0} reclassificado(s).
                </p>
                {esocialCommitResult.skipped.length > 0 && (
                  <p className="mt-1">{esocialCommitResult.skipped.length} lançamento(s) ignorado(s) por já existirem.</p>
                )}
                {esocialCommitResult.errors.map((item) => (
                  <p key={item.index} className="mt-1 text-red-700">{item.description}: {item.error}</p>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Eventos eSocial</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Evento</th>
                  <th className="px-4 py-3 text-left font-medium">Competência</th>
                  <th className="px-4 py-3 text-left font-medium">CPF</th>
                  <th className="px-4 py-3 text-left font-medium">Recibo</th>
                  <th className="px-4 py-3 text-left font-medium">Processado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentEvents.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-center text-gray-500" colSpan={5}>
                      Nenhum evento salvo.
                    </td>
                  </tr>
                )}
                {recentEvents.map((event) => (
                  <tr key={event.id} className="hover:bg-blue-50 even:bg-gray-100">
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-gray-900">{event.tp_evt || '-'}</p>
                      <p className="text-xs text-gray-500 truncate max-w-xs">{event.id}</p>
                    </td>
                    <td className="px-4 py-2.5 text-gray-700">{event.per_apur || '-'}</td>
                    <td className="px-4 py-2.5 text-gray-700">{event.cpf_trab || '-'}</td>
                    <td className="px-4 py-2.5 text-gray-700">{event.nr_recibo || '-'}</td>
                    <td className="px-4 py-2.5">
                      {event.processed_at ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">Sim</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">Não</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  if (!accounts.length) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Importar Transações</h1>
        {activeImportMode === 'file' && step !== 'upload' && (
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Nova importação
          </button>
        )}
      </div>

      <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
        <button
          type="button"
          onClick={() => setActiveImportMode('file')}
          className={`px-4 py-2 text-sm font-medium rounded-md ${
            activeImportMode === 'file'
              ? 'bg-blue-600 text-white'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          Arquivo
        </button>
        <button
          type="button"
          onClick={() => setActiveImportMode('esocial')}
          className={`px-4 py-2 text-sm font-medium rounded-md ${
            activeImportMode === 'esocial'
              ? 'bg-blue-600 text-white'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          eSocial
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {activeImportMode === 'esocial' && renderEsocialPanel()}

      {activeImportMode === 'file' && (
        <div className="contents">

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* File upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Arquivo</label>
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                <Upload className="h-8 w-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">
                  {batchFiles.length > 1
                    ? `${batchFiles.length} arquivos selecionados`
                    : file ? file.name : 'Clique para selecionar (múltiplos PDFs para folha)'}
                </span>
                <input
                  type="file"
                  accept=".csv,.ofx,.qfx,.pdf,.xml"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = e.target.files;
                    if (!files || files.length === 0) return;
                    if (files.length > 1) {
                      // Batch mode: multiple payroll PDFs
                      setBatchFiles(Array.from(files));
                      setFile(files[0]);
                      setFormat('pdf_auto');
                    } else {
                      const f = files[0];
                      setFile(f);
                      setBatchFiles([]);
                      const ext = f.name.split('.').pop()?.toLowerCase();
                      if (ext === 'ofx' || ext === 'qfx') setFormat('ofx');
                      else if (ext === 'xml') setFormat('nf_xml');
                      else if (ext === 'pdf') {
                        const detected = detectPdfFormat(f.name);
                        setFormat(detected);
                        // Auto-select source account based on PDF format
                        const formatAccountMap: Record<string, string> = {
                          'pdf_caixa': 'Ativos:Banco:CEF',
                          'pdf_cdb_santander': 'Ativos:Banco:Santander',
                        };
                        const preferred = formatAccountMap[detected];
                        if (preferred) {
                          const match = accounts.find((a) => a === preferred);
                          if (match) setSourceAccount(match);
                        }
                      }
                      else setFormat('csv');
                    }
                  }}
                />
              </label>
            </div>

            {/* Format & source account */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Formato</label>
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <optgroup label="Extrato Bancário">
                    <option value="csv">CSV</option>
                    <option value="ofx">OFX</option>
                    <option value="pdf_caixa">PDF Caixa</option>
                    <option value="pdf_cdb_santander">PDF CDB Santander</option>
                    <option value="pdf_das">PDF DAS (Simples Nacional)</option>
                  </optgroup>
                  <optgroup label="Folha de Pagamento">
                    <option value="pdf_auto">Auto-detectar (por conteúdo)</option>
                    <option value="pdf_holerite">Holerite (Recibo de Pagamento)</option>
                    <option value="pdf_fgts_relatorio">Relatório FGTS</option>
                    <option value="pdf_darf_federal">DARF Tributos Federais</option>
                    <option value="pdf_fgts_guia">Guia FGTS</option>
                    <option value="pdf_liquidos">Relatório de Líquidos</option>
                  </optgroup>
                  <optgroup label="Notas Fiscais">
                    <option value="nf_xml">XML Nota Fiscal (NFe/NFSe)</option>
                  </optgroup>
                  <optgroup label="Empréstimos">
                    <option value="pdf_cef_loan">Evolução de Dívida CEF</option>
                  </optgroup>
                </select>
              </div>
              {!NO_SOURCE_ACCOUNT_FORMATS.includes(format) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Conta de origem</label>
                  <select
                    value={sourceAccount}
                    onChange={(e) => setSourceAccount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Selecione...</option>
                    {sourceAccounts.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* CSV options */}
          {format === 'csv' && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Opções CSV</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Separador</label>
                  <select
                    value={separator}
                    onChange={(e) => setSeparator(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value=",">Vírgula (,)</option>
                    <option value=";">Ponto e vírgula (;)</option>
                    <option value="\t">Tab</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Formato da data</label>
                  <select
                    value={dateFormat}
                    onChange={(e) => setDateFormat(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="%d/%m/%Y">DD/MM/AAAA</option>
                    <option value="%Y-%m-%d">AAAA-MM-DD</option>
                    <option value="%m/%d/%Y">MM/DD/AAAA</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Col. Data</label>
                  <input
                    type="number"
                    min="0"
                    value={dateCol}
                    onChange={(e) => setDateCol(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Col. Descrição</label>
                  <input
                    type="number"
                    min="0"
                    value={descCol}
                    onChange={(e) => setDescCol(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Col. Valor</label>
                  <input
                    type="number"
                    min="0"
                    value={amountCol}
                    onChange={(e) => setAmountCol(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={skipHeader}
                      onChange={(e) => setSkipHeader(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Pular cabeçalho
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleParse}
              disabled={!file || parsing}
              className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {parsing ? <LoadingSpinner /> : <Upload className="h-4 w-4" />}
              Processar arquivo
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Preview (bank statement) */}
      {step === 'preview' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Preview — {transactions.filter((t) => !t.skip).length}/{transactions.length} transações
              </h2>
              <p className="text-sm text-gray-500">
                Atribua uma conta para cada linha.
                {transactions.filter((t) => t.skip).length > 0 && (
                  <span className="text-amber-600 ml-1">
                    {transactions.filter((t) => t.skip).length} duplicada(s) serão ignoradas.
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                onChange={(e) => {
                  if (e.target.value) handleSetAllCategories(e.target.value);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                defaultValue=""
              >
                <option value="" disabled>Aplicar conta em massa...</option>
                {categoryAccounts.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              <button
                onClick={handleCommit}
                disabled={committing}
                className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
                Confirmar importação
              </button>
            </div>
          </div>
          {bulkDuplicate && (
            <div className="mx-4 mt-3 p-3 rounded-lg text-sm bg-amber-50 border border-amber-300 text-amber-900">
              ⚠ <strong>Possível re-importação:</strong>{' '}
              {transactions.filter(t => t.duplicate_of).length} de {transactions.length} transações
              parecem já estar no journal. Revise os flags antes de confirmar.
            </div>
          )}
          {fitidSkipCount > 0 && (
            <div className="mx-4 mt-3 p-3 rounded-lg text-sm bg-blue-50 border border-blue-300 text-blue-900">
              ℹ <strong>{fitidSkipCount}</strong> transação(ões) detectadas como duplicatas exatas
              (mesmo FITID) — serão puladas automaticamente. Marque <em>Importar</em> manualmente
              se quiser sobrescrever.
            </div>
          )}
          {saldo && (
            <div className={`mx-4 mt-3 p-3 rounded-lg text-sm ${saldo.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              <div className="flex items-center gap-4 flex-wrap">
                <span><strong>Saldo anterior:</strong> {formatAmount(saldo.saldo_anterior)}</span>
                {saldo.saldo_final_pdf !== null && (
                  <span><strong>Saldo final (extrato):</strong> {formatAmount(saldo.saldo_final_pdf)}</span>
                )}
                <span><strong>Saldo calculado:</strong> {formatAmount(saldo.saldo_final_calculado)}</span>
                {!saldo.ok && (
                  <span className="font-semibold text-red-700">
                    Saldo não bate — possível transação faltando!
                  </span>
                )}
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-2 py-3 text-center font-medium w-10">
                    <span title="Importar">Imp.</span>
                  </th>
                  <th className="px-4 py-3 text-left font-medium">Data</th>
                  <th className="px-4 py-3 text-left font-medium">Descrição</th>
                  <th className="px-4 py-3 text-right font-medium">Valor</th>
                  <th className="px-4 py-3 text-left font-medium">Conta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map((txn, i) => (
                  <tr key={i} className={`hover:bg-blue-50 even:bg-gray-100 ${txn.skip ? 'opacity-40' : ''}`}>
                    <td className="px-2 py-2.5 text-center">
                      <input
                        type="checkbox"
                        checked={!txn.skip}
                        onChange={() => handleToggleSkip(i)}
                        className="rounded border-gray-300"
                        title={txn.duplicate_of ? `Possível duplicata de: ${txn.duplicate_of}` : 'Importar esta transação'}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">{txn.date}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-gray-900">{txn.description}</span>
                      {txn.duplicate_of && (() => {
                        const kind = txn.duplicate_kind || '';
                        let label = `duplicata: ${txn.duplicate_of}`;
                        let cls = 'text-amber-700 bg-amber-50 border border-amber-200';
                        if (kind === 'fitid-exact') {
                          label = `duplicata exata (FITID): ${txn.duplicate_of}`;
                          cls = 'text-red-700 bg-red-50 border border-red-200';
                        } else if (kind === 'cross-account') {
                          label = `transferência interna já registrada: ${txn.duplicate_of}`;
                          cls = 'text-orange-700 bg-orange-50 border border-orange-200';
                        }
                        return (
                          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${cls}`} title={label}>
                            {label}
                          </span>
                        );
                      })()}
                      {txn.review_reason === 'estorno_ou_devolucao' && (
                        <span
                          className="ml-2 text-xs text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded"
                          title={txn.review_hint || 'Estorno — revisar manualmente'}
                        >
                          Estorno — revisar manualmente
                        </span>
                      )}
                    </td>
                    <td className={`px-4 py-2.5 text-right whitespace-nowrap font-mono ${
                      txn.amount < 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {formatAmount(txn.amount)}
                    </td>
                    <td className="px-4 py-2.5">
                      <select
                        value={txn.category}
                        onChange={(e) => handleCategoryChange(i, e.target.value)}
                        disabled={txn.skip}
                        className={`w-full px-2 py-1.5 border rounded text-sm ${
                          txn.skip ? 'bg-gray-100 border-gray-200' :
                          txn.category ? 'border-gray-300' : 'border-red-300 bg-red-50'
                        }`}
                      >
                        <option value="">Selecione...</option>
                        {categoryAccounts.map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Step 2b: CDB Preview */}
      {step === 'preview-cdb' && cdbData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Preview — Resgate {cdbData.produto}
              </h2>
              <p className="text-sm text-gray-500">
                Aplicado em {cdbData.data_aplicacao.split('/').reverse().join('/')} — Resgatado em {cdbData.data_resgate.split('/').reverse().join('/')}
              </p>
            </div>
            <button
              onClick={handleCommitCDB}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              {cdbReplace ? 'Substituir transação do extrato' : 'Confirmar importação'}
            </button>
          </div>
          {cdbReplace && (
            <div className="mx-4 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Transação encontrada no extrato — será substituída com os detalhes do CDB
            </div>
          )}
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Valor Aplicado</p>
                <p className="text-lg font-semibold text-gray-900">{formatAmount(cdbData.valor_aplicado)}</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-xs text-green-600">Rendimento Bruto</p>
                <p className="text-lg font-semibold text-green-700">{formatAmount(cdbData.rendimento_bruto)}</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-xs text-red-600">IOF</p>
                <p className="text-lg font-semibold text-red-700">{formatAmount(cdbData.iof)}</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-xs text-red-600">IR Fonte</p>
                <p className="text-lg font-semibold text-red-700">{formatAmount(cdbData.ir)}</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs text-blue-600">Valor Líquido</p>
                <p className="text-lg font-semibold text-blue-700">{formatAmount(cdbData.valor_resgate_liquido)}</p>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Lançamento</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Conta</th>
                    <th className="px-4 py-2 text-right font-medium">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cdbData.transaction.postings.map((p, i) => (
                    <tr key={i} className="hover:bg-blue-50 even:bg-gray-100">
                      <td className="px-4 py-2 text-gray-900">{p.account}</td>
                      <td className={`px-4 py-2 text-right font-mono ${p.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatAmount(p.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Step 2c: DAS Preview */}
      {step === 'preview-das' && dasData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Preview — DAS Simples Nacional - {dasData.periodo}
              </h2>
              <p className="text-sm text-gray-500">
                Vencimento: {dasData.data_vencimento.split('/').reverse().join('/')}
                {' — '}Total: {formatAmount(dasData.valor_total)}
              </p>
            </div>
            <button
              onClick={handleCommitDAS}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              {dasReplace ? 'Substituir transação do extrato' : 'Confirmar importação'}
            </button>
          </div>
          {dasReplace && (
            <div className="mx-4 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Transação encontrada no extrato — será substituída com os detalhes do DAS
            </div>
          )}
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {dasData.tributos.map((t) => (
                <div key={t.codigo} className="bg-red-50 p-3 rounded-lg">
                  <p className="text-xs text-red-600">{t.nome}</p>
                  <p className="text-lg font-semibold text-red-700">{formatAmount(t.valor)}</p>
                </div>
              ))}
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Lançamento</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Conta</th>
                    <th className="px-4 py-2 text-right font-medium">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {dasData.transaction.postings.map((p, i) => (
                    <tr key={i} className="hover:bg-blue-50 even:bg-gray-100">
                      <td className="px-4 py-2 text-gray-900">{p.account}</td>
                      <td className={`px-4 py-2 text-right font-mono ${p.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatAmount(p.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Payroll Preview */}
      {step === 'preview-payroll' && payrollData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Provisão Folha — Competência {payrollData.competencia}
              </h2>
              <p className="text-sm text-gray-500">
                {payrollData.funcionarios.length} funcionário(s) — Total bruto: {formatAmount(payrollData.funcionarios.reduce((s, f) => s + f.vencimentos, 0))}
              </p>
            </div>
            <button
              onClick={handleCommitPayroll}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              Confirmar provisão
            </button>
          </div>
          {payrollWarnings.length > 0 && (
            <div className="mx-4 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  {payrollWarnings.map((w, i) => <p key={i}>{w}</p>)}
                </div>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-2 py-3 text-center font-medium w-10">Imp.</th>
                  <th className="px-4 py-3 text-left font-medium">Funcionário</th>
                  <th className="px-4 py-3 text-left font-medium">Tipo</th>
                  <th className="px-4 py-3 text-right font-medium">Vencimentos</th>
                  <th className="px-4 py-3 text-right font-medium">INSS</th>
                  <th className="px-4 py-3 text-right font-medium">IRRF</th>
                  <th className="px-4 py-3 text-right font-medium">Líquido</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payrollData.funcionarios.map((func, i) => (
                  <tr key={func.nome} className={`hover:bg-blue-50 even:bg-gray-100 ${payrollSkip[func.nome] ? 'opacity-40' : ''}`}>
                    <td className="px-2 py-2.5 text-center">
                      <input
                        type="checkbox"
                        checked={!payrollSkip[func.nome]}
                        onChange={() => setPayrollSkip(prev => ({ ...prev, [func.nome]: !prev[func.nome] }))}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-2.5 text-gray-900">{formatBrazilianName(func.nome)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded ${func.tipo === 'socio' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>
                        {func.tipo === 'socio' ? 'Sócio' : 'CLT'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-900">{formatAmount(func.vencimentos)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-red-600">{func.inss > 0 ? formatAmount(func.inss) : '-'}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-red-600">{func.irrf > 0 ? formatAmount(func.irrf) : '-'}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-green-700">{formatAmount(func.liquido)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td className="px-2 py-2.5" />
                  <td className="px-4 py-2.5 text-gray-900" colSpan={2}>Total</td>
                  <td className="px-4 py-2.5 text-right font-mono">{formatAmount(payrollData.funcionarios.reduce((s, f) => s + f.vencimentos, 0))}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-red-600">{formatAmount(payrollData.funcionarios.reduce((s, f) => s + f.inss, 0))}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-red-600">{formatAmount(payrollData.funcionarios.reduce((s, f) => s + f.irrf, 0))}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-green-700">{formatAmount(payrollData.funcionarios.reduce((s, f) => s + f.liquido, 0))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* FGTS Preview */}
      {step === 'preview-fgts' && fgtsData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Provisão FGTS — Competência {fgtsData.competencia}
              </h2>
              <p className="text-sm text-gray-500">
                {fgtsData.funcionarios.length} funcionário(s) — Total FGTS: {formatAmount(fgtsData.total_fgts)}
              </p>
            </div>
            <button
              onClick={handleCommitFGTS}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              Confirmar provisão FGTS
            </button>
          </div>
          {payrollWarnings.length > 0 && (
            <div className="mx-4 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>{payrollWarnings.map((w, i) => <p key={i}>{w}</p>)}</div>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Funcionário</th>
                  <th className="px-4 py-3 text-right font-medium">Remuneração</th>
                  <th className="px-4 py-3 text-right font-medium">FGTS (8%)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {fgtsData.funcionarios.map((func, i) => (
                  <tr key={i} className="hover:bg-blue-50 even:bg-gray-100">
                    <td className="px-4 py-2.5 text-gray-900">{formatBrazilianName(func.nome)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-700">{formatAmount(func.remuneracao)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-blue-700">{formatAmount(func.fgts)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td className="px-4 py-2.5 text-gray-900">Total</td>
                  <td className="px-4 py-2.5 text-right font-mono">{formatAmount(fgtsData.funcionarios.reduce((s, f) => s + f.remuneracao, 0))}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-blue-700">{formatAmount(fgtsData.total_fgts)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* DARF Preview */}
      {step === 'preview-darf' && darfData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Provisão Tributos Federais — Competência {darfData.competencia}
              </h2>
              <p className="text-sm text-gray-500">
                Total: {formatAmount(darfData.valor_total)}
              </p>
            </div>
            <button
              onClick={handleCommitDARF}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              Confirmar provisão tributos
            </button>
          </div>
          {payrollWarnings.length > 0 && (
            <div className="mx-4 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>{payrollWarnings.map((w, i) => <p key={i}>{w}</p>)}</div>
              </div>
            </div>
          )}
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {darfData.tributos.map((t) => (
                <div key={t.codigo} className="bg-red-50 p-3 rounded-lg">
                  <p className="text-xs text-red-600">{t.codigo} — {t.nome}</p>
                  <p className="text-lg font-semibold text-red-700">{formatAmount(t.valor)}</p>
                </div>
              ))}
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Lançamento</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Conta</th>
                    <th className="px-4 py-2 text-right font-medium">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {darfData.transaction.postings.map((p, i) => (
                    <tr key={i} className="hover:bg-blue-50 even:bg-gray-100">
                      <td className="px-4 py-2 text-gray-900">{p.account}</td>
                      <td className={`px-4 py-2 text-right font-mono ${p.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatAmount(p.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* FGTS Guia Preview (validation only) */}
      {step === 'preview-fgts-guia' && fgtsGuiaData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Guia FGTS — Competência {fgtsGuiaData.competencia}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-xs text-blue-600">Valor a Recolher</p>
              <p className="text-2xl font-semibold text-blue-700">{formatAmount(fgtsGuiaData.valor_total)}</p>
            </div>
            {fgtsGuiaData.data_vencimento && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-500">Vencimento</p>
                <p className="text-2xl font-semibold text-gray-900">{fgtsGuiaData.data_vencimento.split('/').reverse().join('/')}</p>
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500">
            A guia FGTS é apenas para validação — a provisão é gerada pelo Relatório FGTS.
          </p>
        </div>
      )}

      {/* Líquidos Preview (validation only) */}
      {step === 'preview-liquidos' && liquidosData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Relatório de Líquidos — Competência {liquidosData.competencia}
            </h2>
            <p className="text-sm text-gray-500">
              {liquidosData.funcionarios.length} funcionário(s) — Total: {formatAmount(liquidosData.total)}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Código</th>
                  <th className="px-4 py-3 text-left font-medium">Nome</th>
                  <th className="px-4 py-3 text-right font-medium">Valor Líquido</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {liquidosData.funcionarios.map((func, i) => (
                  <tr key={i} className="hover:bg-blue-50 even:bg-gray-100">
                    <td className="px-4 py-2.5 text-gray-500">{func.codigo}</td>
                    <td className="px-4 py-2.5 text-gray-900">{formatBrazilianName(func.nome)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-green-700">{formatAmount(func.valor)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td className="px-4 py-2.5" />
                  <td className="px-4 py-2.5 text-gray-900">Total</td>
                  <td className="px-4 py-2.5 text-right font-mono text-green-700">{formatAmount(liquidosData.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-500">
              O relatório de líquidos é apenas para validação cruzada — as provisões são geradas pelo holerite.
            </p>
          </div>
        </div>
      )}

      {/* CEF Loan Preview */}
      {step === 'preview-cef-loan' && cefLoanData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Evolução de Dívida — {cefLoanData.contrato.produto || 'Contrato CEF'}
            </h2>
            <div className="text-sm text-gray-500 mt-1 space-y-0.5">
              <p>Contrato: {cefLoanData.contrato.numero} — Valor: {formatAmount(cefLoanData.contrato.valor_contrato)}</p>
              <p>
                {cefLoanData.resumo.parcelas_pagas}/{cefLoanData.resumo.parcelas_total} parcelas pagas —{' '}
                Total pago: {formatAmount(cefLoanData.resumo.total_pago)} —{' '}
                Saldo devedor: {formatAmount(cefLoanData.resumo.saldo_devedor)}
              </p>
              <p className="text-xs text-gray-400">
                Amortização: {formatAmount(cefLoanData.resumo.total_amortizacao)} |
                Juros: {formatAmount(cefLoanData.resumo.total_juros)} |
                Encargos: {formatAmount(cefLoanData.resumo.total_encargos)}
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-3 py-2.5 text-left font-medium">Parcela</th>
                  <th className="px-3 py-2.5 text-left font-medium">Vencimento</th>
                  <th className="px-3 py-2.5 text-center font-medium">Status</th>
                  <th className="px-3 py-2.5 text-right font-medium">Amortização</th>
                  <th className="px-3 py-2.5 text-right font-medium">Juros</th>
                  <th className="px-3 py-2.5 text-right font-medium">Encargos</th>
                  <th className="px-3 py-2.5 text-right font-medium">Total Pago</th>
                  <th className="px-3 py-2.5 text-right font-medium">Saldo Devedor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cefLoanData.parcelas.map((p: any, i: number) => (
                  <tr key={i} className={`hover:bg-blue-50 ${i % 2 === 1 ? 'bg-gray-50' : ''}`}>
                    <td className="px-3 py-2 text-gray-700 font-medium">{p.numero}</td>
                    <td className="px-3 py-2 text-gray-600">{p.vencimento}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        p.status === 'PG' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-gray-800">{p.status === 'PG' ? formatAmount(p.amortizacao) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-orange-600">{p.status === 'PG' ? formatAmount(p.juros) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-red-600">{p.encargos > 0 ? formatAmount(p.encargos) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono font-medium text-gray-900">{p.status === 'PG' ? formatAmount(p.valor_total_pago) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-blue-600">{p.saldo_devedor > 0 ? formatAmount(p.saldo_devedor) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-4 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Cria competência (juros no vencimento) + reclassifica pagamento (D: Passivo / C: Banco).
              Cria saldo de abertura se o contrato ainda não estiver no ledger.
            </p>
            <button
              onClick={async () => {
                setCommitting(true);
                setError('');
                try {
                  const res = await commitCEFLoanImport({
                    contrato_numero: cefLoanData.contrato.numero,
                    contrato_produto: cefLoanData.contrato.produto,
                    contrato_valor: cefLoanData.contrato.valor_contrato,
                    parcelas: cefLoanData.parcelas,
                  });
                  setResult({
                    created: res.reclassificadas.length + res.competencia_criadas.length + (res.inicial === 'criado_saldo_abertura' ? 1 : 0),
                    errors: res.errors.map((e: any) => ({ index: e.parcela ?? 0, error: `${e.step ? `[${e.step}] ` : ''}${e.error}` })),
                  });
                  setStep('done');
                } catch (err: any) {
                  setError(err.response?.data?.detail || 'Erro ao importar empréstimo');
                } finally {
                  setCommitting(false);
                }
              }}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              Importar empréstimo
            </button>
          </div>
        </div>
      )}

      {/* NF XML Preview */}
      {step === 'preview-nf' && nfData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {nfData.tipo === 'nfe' ? 'NFe' : 'NFSe'} — NF {nfData.numero}
            </h2>
            <div className="text-sm text-gray-500 mt-1 space-y-0.5">
              <p>Data: {nfData.data_emissao} — Competência: {nfData.competencia}</p>
              <p>
                Valor: <span className="font-mono font-medium text-gray-900">{formatAmount(nfData.valor_total)}</span>
                {nfData.moeda_servico && nfData.moeda_servico !== 'BRL' && nfData.valor_servico_moeda != null && (
                  <span className="ml-2 text-xs text-gray-500">
                    ({nfData.moeda_servico} {nfData.valor_servico_moeda.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                  </span>
                )}
              </p>
              {nfData.nome_destinatario && <p>Destinatário: {nfData.nome_destinatario}{nfData.cnpj_destinatario ? ` (${nfData.cnpj_destinatario})` : ''}</p>}
              {nfData.cliente && (
                <p>
                  Cliente detectado: <span className="font-medium text-blue-700">{nfData.cliente}</span>
                  {nfData.modalidade && (
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                      nfData.modalidade === 'pre_recebimento' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {nfData.modalidade === 'pre_recebimento' ? 'Pré-recebimento' : 'Pós-recebimento'}
                    </span>
                  )}
                </p>
              )}
              {nfData.descricao && <p className="text-xs text-gray-400 truncate">Serviço: {nfData.descricao}</p>}
              {(nfData.iss_retido || nfData.municipio_incidencia || nfData.codigo_tributario) && (
                <p className="text-xs text-gray-500 flex flex-wrap gap-x-3">
                  {nfData.municipio_incidencia && <span>Município: {nfData.municipio_incidencia}</span>}
                  {nfData.iss_retido && (
                    <span>
                      ISS retido: <span className={nfData.iss_retido === 'sim' ? 'font-medium text-amber-700' : 'text-gray-600'}>{nfData.iss_retido}</span>
                    </span>
                  )}
                  {nfData.codigo_tributario && <span>Cód. trib.: {nfData.codigo_tributario}</span>}
                </p>
              )}
            </div>
            {nfData.competencia_estimada && (
              <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                ⚠ Competência estimada da data de emissão. Confirme se está correta.
              </div>
            )}
          </div>

          {/* Transaction details */}
          <div className="p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">Lançamentos a criar:</h3>

            {/* Main transaction: Contas a Receber ↔ Receita */}
            <div className="bg-blue-50 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-800 mb-1">1. Reconhecimento de Receita</p>
              {nfData.transaction.postings.map((p, i) => (
                <div key={i} className="flex justify-between text-blue-700">
                  <span>{p.account}</span>
                  <span className="font-mono">{p.amount >= 0 ? '+' : ''}{formatAmount(p.amount)}</span>
                </div>
              ))}
            </div>

            {/* Provisão clearing */}
            {nfData.has_provisao && nfData.provisao_postings.length > 0 && (
              <div className="bg-amber-50 rounded-lg p-3 text-sm">
                <label className="flex items-center gap-2 mb-1">
                  <input
                    type="checkbox"
                    checked={nfClearProvisao}
                    onChange={(e) => setNfClearProvisao(e.target.checked)}
                    className="rounded"
                  />
                  <span className="font-medium text-amber-800">2. Baixar Provisão a Faturar</span>
                </label>
                {nfClearProvisao && nfData.provisao_postings.map((p, i) => (
                  <div key={i} className="flex justify-between text-amber-700">
                    <span>{p.account}</span>
                    <span className="font-mono">{p.amount >= 0 ? '+' : ''}{formatAmount(p.amount)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Adiantamento clearing */}
            {nfData.has_adiantamento && (
              <div className="bg-green-50 rounded-lg p-3 text-sm">
                <label className="flex items-center gap-2 mb-1">
                  <input
                    type="checkbox"
                    checked={nfClearAdiantamento}
                    onChange={(e) => setNfClearAdiantamento(e.target.checked)}
                    className="rounded"
                  />
                  <span className="font-medium text-green-800">
                    {nfData.has_provisao ? '3' : '2'}. Baixar Adiantamento de Cliente
                    {nfData.adiantamento_balance != null && (
                      <span className="text-xs font-normal ml-1">(saldo: {formatAmount(Math.abs(nfData.adiantamento_balance))})</span>
                    )}
                  </span>
                </label>
                {nfClearAdiantamento && nfData.cliente && (
                  <div className="space-y-0.5">
                    <div className="flex justify-between text-green-700">
                      <span>Passivo:Adiantamentos de Clientes:{nfData.cliente}</span>
                      <span className="font-mono">+{formatAmount(nfData.valor_total)}</span>
                    </div>
                    <div className="flex justify-between text-green-700">
                      <span>Ativos:Contas a Receber:{nfData.cliente}:{nfData.competencia}</span>
                      <span className="font-mono">-{formatAmount(nfData.valor_total)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="p-4 flex items-center justify-between border-t border-gray-200">
            <button
              onClick={() => { setStep('upload'); setNfData(null); }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Voltar
            </button>
            <button
              onClick={async () => {
                if (!nfData) return;
                setCommitting(true);
                setError('');
                try {
                  const provisaoPostings = nfClearProvisao ? nfData.provisao_postings : [];
                  const adiantamentoPostings = nfClearAdiantamento && nfData.cliente ? [
                    { account: `Passivo:Adiantamentos de Clientes:${nfData.cliente}`, amount: nfData.valor_total, commodity: 'BRL' },
                    { account: `Ativos:Contas a Receber:${nfData.cliente}:${nfData.competencia}`, amount: -nfData.valor_total, commodity: 'BRL' },
                  ] : [];
                  // Read XML to attach as document
                  let xmlFilename = '';
                  let xmlBase64 = '';
                  if (file) {
                    const buf = await file.arrayBuffer();
                    const bytes = new Uint8Array(buf);
                    let binary = '';
                    for (let i = 0; i < bytes.byteLength; i++) {
                      binary += String.fromCharCode(bytes[i]);
                    }
                    xmlBase64 = btoa(binary);
                    xmlFilename = file.name;
                  }
                  const res = await commitNFImport({
                    date: nfData.transaction.date,
                    description: nfData.transaction.description,
                    postings: nfData.transaction.postings,
                    provisao_postings: provisaoPostings,
                    adiantamento_postings: adiantamentoPostings,
                    xml_filename: xmlFilename,
                    xml_content_base64: xmlBase64,
                    nfse_numero: nfData.numero || undefined,
                    nfse_competencia: nfData.competencia || undefined,
                    nfse_emitida_em: nfData.data_emissao_completa || undefined,
                    nfse_tomador_cnpj: nfData.cnpj_destinatario || undefined,
                    nfse_tomador_nome: nfData.nome_destinatario || undefined,
                    nfse_valor_bruto: nfData.valor_total || undefined,
                    nfse_valor_liquido: nfData.valor_liquido || undefined,
                    nfse_iss_retido: nfData.iss_retido || undefined,
                    nfse_municipio_incidencia: nfData.municipio_incidencia || undefined,
                    nfse_cod_tributario: nfData.codigo_tributario || undefined,
                  });
                  setResult({ created: res.created, errors: res.errors.map((e: any, i: number) => ({ index: i, error: e.error })) });
                  setStep('done');
                } catch (err: any) {
                  setError(err.response?.data?.detail || 'Erro ao importar NF');
                } finally {
                  setCommitting(false);
                }
              }}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              Importar NF
            </button>
          </div>
        </div>
      )}

      {/* Batch Preview */}
      {step === 'preview-batch' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Importação em Lote — Folha de Pagamento
              </h2>
              <p className="text-sm text-gray-500">
                {batchFiles.length} arquivo(s) processado(s)
                {batchPayroll && ` — Competência ${batchPayroll.competencia}`}
              </p>
            </div>
            <button
              onClick={handleCommitBatch}
              disabled={committing}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {committing ? <LoadingSpinner /> : <Check className="h-4 w-4" />}
              Confirmar todas as provisões
            </button>
          </div>

          {batchWarnings.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  {batchWarnings.map((w, i) => <p key={i}>{w}</p>)}
                </div>
              </div>
            </div>
          )}

          {/* Holerite section */}
          {batchPayroll && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-3 border-b border-gray-200 bg-blue-50">
                <h3 className="text-sm font-semibold text-blue-900">Holerite — {batchPayroll.funcionarios.length} funcionário(s)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-2 py-2 text-center font-medium w-10">Imp.</th>
                      <th className="px-3 py-2 text-left font-medium">Funcionário</th>
                      <th className="px-3 py-2 text-right font-medium">Vencimentos</th>
                      <th className="px-3 py-2 text-right font-medium">INSS</th>
                      <th className="px-3 py-2 text-right font-medium">IRRF</th>
                      <th className="px-3 py-2 text-right font-medium">Líquido</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {batchPayroll.funcionarios.map((func, i) => (
                      <tr key={func.nome} className={`hover:bg-blue-50 even:bg-gray-100 ${batchPayrollSkip[func.nome] ? 'opacity-40' : ''}`}>
                        <td className="px-2 py-2 text-center">
                          <input type="checkbox" checked={!batchPayrollSkip[func.nome]}
                            onChange={() => setBatchPayrollSkip(prev => ({ ...prev, [func.nome]: !prev[func.nome] }))}
                            className="rounded border-gray-300" />
                        </td>
                        <td className="px-3 py-2 text-gray-900">{formatBrazilianName(func.nome)}</td>
                        <td className="px-3 py-2 text-right font-mono">{formatAmount(func.vencimentos)}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-600">{func.inss > 0 ? formatAmount(func.inss) : '-'}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-600">{func.irrf > 0 ? formatAmount(func.irrf) : '-'}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-green-700">{formatAmount(func.liquido)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* FGTS section */}
          {batchFgts && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-3 border-b border-gray-200 bg-blue-50">
                <h3 className="text-sm font-semibold text-blue-900">FGTS — Total: {formatAmount(batchFgts.total_fgts)}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Funcionário</th>
                      <th className="px-3 py-2 text-right font-medium">Remuneração</th>
                      <th className="px-3 py-2 text-right font-medium">FGTS (8%)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {batchFgts.funcionarios.map((func, i) => (
                      <tr key={i} className="hover:bg-blue-50 even:bg-gray-100">
                        <td className="px-3 py-2 text-gray-900">{formatBrazilianName(func.nome)}</td>
                        <td className="px-3 py-2 text-right font-mono">{formatAmount(func.remuneracao)}</td>
                        <td className="px-3 py-2 text-right font-mono text-blue-700">{formatAmount(func.fgts)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* DARF section */}
          {batchDarf && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-3 border-b border-gray-200 bg-red-50">
                <h3 className="text-sm font-semibold text-red-900">DARF Tributos — Total: {formatAmount(batchDarf.valor_total)}</h3>
              </div>
              <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                {batchDarf.tributos.map((t) => (
                  <div key={t.codigo} className="bg-red-50 p-3 rounded-lg">
                    <p className="text-xs text-red-600">{t.codigo} — {t.nome}</p>
                    <p className="text-lg font-semibold text-red-700">{formatAmount(t.valor)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FGTS Guia validation */}
          {batchFgtsGuia && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900">Guia FGTS (validação)</h3>
              <p className="text-sm text-gray-500 mt-1">Valor a recolher: {formatAmount(batchFgtsGuia.valor_total)}</p>
            </div>
          )}

          {/* Líquidos validation */}
          {batchLiquidos && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900">Relatório de Líquidos (validação)</h3>
              <p className="text-sm text-gray-500 mt-1">{batchLiquidos.funcionarios.length} funcionário(s) — Total: {formatAmount(batchLiquidos.total)}</p>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Done */}
      {step === 'done' && result && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Importação concluída</h2>
          <p className="text-gray-600 mb-1">
            {result.created} transação(ões) importada(s) com sucesso.
          </p>
          <p className="text-sm text-gray-500">
            As transações foram marcadas como pendentes (!) para conciliação posterior.
          </p>
          {(result as any).skipped?.length > 0 && (
            <p className="text-sm text-amber-600 mt-1">
              {(result as any).skipped.length} transação(ões) ignorada(s) (já existiam).
            </p>
          )}
          {docAttached === true && (
            <p className="text-sm text-green-600 mt-1 flex items-center justify-center gap-1">
              <Paperclip className="h-3.5 w-3.5" />
              {(() => {
                const n = (result as any)?.docLinkCount;
                const missed = (result as any)?.docMissedCount;
                if (typeof n === 'number') {
                  return `Documento fonte anexado a ${n} transação(ões)${missed ? ` (${missed} sem match)` : ''}.`;
                }
                return 'Documento fonte anexado automaticamente.';
              })()}
            </p>
          )}
          {docAttached === false && docAttachError && (
            <p className="text-sm text-amber-600 mt-1 flex items-center justify-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" />
              {docAttachError}
            </p>
          )}
          {employeeSync && (employeeSync.created.length > 0 || employeeSync.updated.length > 0 || employeeSync.deactivated.length > 0) && (
            <div className="mt-4 text-left bg-blue-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-blue-700 mb-2">Cadastro de funcionários atualizado:</p>
              {employeeSync.created.length > 0 && (
                <p className="text-sm text-blue-600">
                  {employeeSync.created.length} novo(s): {employeeSync.created.map((e: any) => e.name).join(', ')}
                </p>
              )}
              {employeeSync.updated.length > 0 && (
                <p className="text-sm text-blue-600">
                  {employeeSync.updated.length} atualizado(s): {employeeSync.updated.map((e: any) => e.id).join(', ')}
                </p>
              )}
              {employeeSync.deactivated.length > 0 && (
                <p className="text-sm text-amber-600">
                  {employeeSync.deactivated.length} desativado(s): {employeeSync.deactivated.map((e: any) => e.name).join(', ')}
                </p>
              )}
            </div>
          )}
          {result.errors.length > 0 && (
            <div className="mt-4 text-left bg-red-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-red-700 mb-2">
                {result.errors.length} erro(s):
              </p>
              {result.errors.map((e, i) => (
                <p key={i} className="text-sm text-red-600">
                  Linha {e.index + 1}: {e.error}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
        </div>
      )}
    </div>
  );
}
